import {
  BaseFormAction,
  BaseInput,
  BaseTextarea,
  BaseTextField,
  Button,
  Field,
  Radio,
  RadioGroup,
  Switch,
} from '@/components';
import { useBreadcrumbs, useConfirm, useLoadingOverlay, useUnsavedChanges } from '@/hooks';
import { trans } from '@/i18n';
import styles from '@/pages/account/styles/user-page.module.scss';
import updateNoteValidate from '@/pages/note/utils/updateNote.validate';
import { ROUTE_PATH } from '@/routes';
import { NoteService } from '@/services/note';
import { INote, INoteForm } from '@/types';
import { NotePostStatus } from '@/types/enums';
import { INotePostFilter, INotePosts, INotePostsPage } from '@/types/notePost';
import { images, showError, showSuccess } from '@/utils';
import { Box, Flex, Grid, Image, Stack, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { SCROLL_NEAR_BOTTOM_THRESHOLD } from '@/constants/common';

// Initial query note post
const initialQuery: INotePostFilter = {
  page: 1,
  size: 16,
  orderKey: 'publishedAt',
  orderBy: 'desc',
};

function NoteEditPage() {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const [loading, setLoading] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const { toggleConfirm } = useConfirm();
  const [noteDetail, setNoteDetail] = useState<INote>();
  const { toggleLoading } = useLoadingOverlay();
  const { setUnsavedChanges, resetChanges } = useUnsavedChanges();
  const sv = new NoteService();

  const [notePosts, setNotesPosts] = useState<INotePostsPage>({
    data: [],
    loading: false,
    query: initialQuery,
  });
  const [totalPages, setTotalPages] = useState<number>(0);
  const queryRef = useRef<INotePostFilter>(initialQuery);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm<INoteForm>({
    mode: 'onBlur',
    resolver: updateNoteValidate,
  });

  useEffect(() => {
    setUnsavedChanges(isDirty);

    return () => {
      resetChanges();
    };
  }, [isDirty, setUnsavedChanges]);

  const extractNoteUserSegment = (input: string | undefined) => {
    if (!input) return '';
    const prefix = 'https://note.com/';
    if (input.startsWith(prefix)) {
      const path = input.slice(prefix.length);
      return path.split('/')[0];
    }
    // Remove http:// or https:// prefix
    const urlWithoutProtocol = input.replace(/^https?:\/\//, '');

    // Extract only the domain (before the first slash)
    const domain = urlWithoutProtocol.split('/')[0];

    return domain;
  };
  const handleChangeIsActive = ({ value }: { value: string }) => {
    setIsDisabled(true);
    setIsActive(value == 'ACTIVE');
  };

  const handleCheckedSwitch = async (id: string, checked: boolean) => {
    if (!checked) {
      toggleConfirm({
        title: 'note記事非表示',
        message: 'この記事を非表示にしてもよろしいでしょうか？',
        okText: 'はい',
        cancelText: 'いいえ',
        onOk: async function () {
          await sv.updateNotePost(id, { status: NotePostStatus.REJECTED });
          setNotesPosts((prev) => ({
            ...prev,
            data: prev.data.map((p) => ({
              ...p,
              status: p.id === id ? NotePostStatus.REJECTED : p.status,
            })),
          }));
          return;
        },
      });
      return;
    }

    await sv.updateNotePost(id, { status: NotePostStatus.APPROVED });
    setNotesPosts((prev) => ({
      ...prev,
      data: prev.data.map((p) => ({
        ...p,
        status: p.id === id ? NotePostStatus.APPROVED : p.status,
      })),
    }));
  };

  const stripImages = (html: string) => {
    return html?.replace(/<img[^>]*>/g, '') || '';
  };

  const getDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res: INote = await sv.getDetail(id!);

      reset({
        name: res.name,
        link: res.link,
        memo: res.memo,
        isActive: res.isActive,
      });
      setNoteDetail(res);
      setIsActive(res.isActive);
    } catch (error) {
      showError(error);
      navigate(ROUTE_PATH.NOTE);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const getNotePosts = useCallback(
    async (params: INotePostFilter, append = false) => {
      try {
        setLoading(true);
        setNotesPosts((prev) => ({ ...prev, loading: true }));
        queryRef.current = params;
        const response: INotePosts = await sv.getNotePosts(id!, params);
        const newData = response?.notePosts || [];
        setTotalPages(response?.totalPages || 0);
        setNotesPosts((prev) => ({
          ...prev,
          data: append ? [...prev.data, ...newData] : newData,
          query: params,
          loading: false,
        }));
      } catch (error) {
        showError(error);
        setNotesPosts((prev) => ({ ...prev, loading: false }));
        navigate(ROUTE_PATH.NOTE);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const onSubmit: SubmitHandler<INoteForm> = async (data) => {
    try {
      toggleLoading(true);
      const bodyNote: INoteForm = {
        name: data?.name?.trim(),
        link: data?.link?.trim(),
        memo: data?.memo?.trim() ?? '',
        isActive,
      };

      await sv.updateData(id!, bodyNote);
      showSuccess(trans('shared.updateSuccess'));
      navigate(ROUTE_PATH.NOTE);
    } catch (error) {
      showError(error);
      reset({...data, link: '' });
    } finally {
      toggleLoading(false);
    }
  };

  const confirmSubmit: SubmitHandler<INoteForm> = async (data) => {
    try {
      if (!isActive && noteDetail?.isActive !== isActive) {
        toggleConfirm({
          title: 'noteアカウント非表示',
          message: '紐づくページがすべて非表示になりますが、\nよろしいでしょうか？',
          okText: 'はい',
          cancelText: 'いいえ',
          onOk: async () => {
            onSubmit(data);
          },
        });
      } else {
        onSubmit(data);
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (notePosts.loading) return;

    const currentPage = queryRef.current.page || 1;
    if (totalPages > 0 && currentPage >= totalPages) return;

    const nextPage = currentPage + 1;
    const nextQuery = {
      ...queryRef.current,
      page: nextPage,
    };

    getNotePosts(nextQuery, true);
  }, [notePosts.loading, totalPages]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    const isNearBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_NEAR_BOTTOM_THRESHOLD;

    if (isNearBottom) {
      handleLoadMore();
    }
  }, [handleLoadMore]);

  useEffect(() => {
    getDetail();
    getNotePosts(
      {
        ...notePosts.query,
        page: 1,
      },
      false
    );
  }, [id]);

  useEffect(() => {
    setBreadcrumbs(ROUTE_PATH.NOTE, [
      { name: trans('menu.note'), path: ROUTE_PATH.NOTE },
      { name: extractNoteUserSegment(noteDetail?.link), path: `${ROUTE_PATH.NOTE}/${id}/edit` },
      { name: trans('menu.noteDetail'), subname: trans('menu.noteSubDetail') },
    ]);
  }, [id, noteDetail]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    if (noteDetail && noteDetail?.isActive !== isActive) {
      setIsDisabled(true);
    } else {
      setIsDisabled(isDirty);
    }
  }, [isDirty, noteDetail]);

  return (
    <Box
      width="full"
      alignContent="center"
      ref={containerRef}
      overflowY="auto"
      maxH="calc(100vh - 120px)"
    >
      <Box className="box-create-content box-note-edit" width="938px" marginLeft="2%">
        <Flex align="center">
          <div className="st-title">{trans('note.aboutMe')}</div>
        </Flex>
        <form onSubmit={handleSubmit(confirmSubmit)}>
          <Grid gap={'17px'}>
            <Flex justifyContent="space-between">
              <BaseTextField
                label={trans('note.link')}
                value={noteDetail?.link}
                border="none"
                color="#6A6A6A"
              />
              <BaseInput
                errors={errors}
                control={control}
                name="name"
                label={trans('note.name')}
                width="48%"
                span="auto"
                placeholder="例）NINE"
                isRequired
              />
            </Flex>
            <Flex justifyContent="space-between">
              <BaseTextarea
                errors={errors}
                control={control}
                name="memo"
                label={trans('note.memo')}
                width="100%"
                span="auto"
                placeholder="必要に応じてメモを設定できます"
              />
            </Flex>

            <Flex justifyContent="space-between">
              <Field label={trans('note.status')}>
                <RadioGroup
                  value={isActive ? 'ACTIVE' : 'NOT_ACTIVE'}
                  onValueChange={(value: any) => handleChangeIsActive(value)}
                  colorPalette="green"
                  defaultValue="react"
                  size={'xs'}
                  className={styles.radioGroup}
                >
                  <Radio value="ACTIVE">表示</Radio>
                  <Radio value="NOT_ACTIVE">非表示</Radio>
                </RadioGroup>
              </Field>
              <BaseFormAction
                flex={1}
                justifyContent="flex-end"
                marginTop={0}
                loading={loading}
                isAdd={true}
                path={ROUTE_PATH.NOTE}
                txtAdd="note.btnUpdate"
                disabledSubmit={!isDisabled}
                children={
                  <Button
                    width={100}
                    height={35}
                    type="button"
                    colorPalette="red"
                    bgColor="#E53E3E"
                    variant="outline"
                    onClick={() =>
                      toggleConfirm({
                        title: 'noteアカウント削除',
                        message: '削除すると復元できませんがよろしいでしょうか？',
                        okText: '削除',
                        cancelText: 'キャンセル',
                        widthCancel: '160px',
                        onOk: async () => {
                          try {
                            await sv.delete(id!);
                            showSuccess(trans('shared.deleteSuccess'));
                            navigate(ROUTE_PATH.NOTE);
                          } catch (error) {
                            showError(error);
                          }
                        },
                      })
                    }
                  >
                    <Text fontSize={14} fontWeight="bold" color="#FFFFFF">
                      {trans('note.btnDelete')}
                    </Text>
                  </Button>
                }
              />
            </Flex>
          </Grid>
        </form>
      </Box>

      <Box className="box-create-content box-note-edit" w="96%" marginLeft="2%">
        <Flex align="center">
          <div className="st-title" color="#2D3748">
            {trans('note.txtNoteList')}
          </div>
        </Flex>
        <Grid className='note-article-grid' gap="24px" templateColumns="repeat(4, 1fr)">
          {notePosts.data.map((post) => (
            <Box className="box-note-detail">
              {!post.note?.isActive && <div className='box-note-detail-overlay'></div>}
              <Stack direction="column" gap={0} flex={1}>
                <Box position="relative" borderRadius="12px" height="191.5px" width="100%">
                  <Image
                    src={post?.thumbnailUrl ?? images.noImage}
                    alt={post.title}
                    borderRadius="12px"
                    height="191.5px"
                    objectFit="cover"
                    width="100%"
                  />
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    borderRadius="12px"
                  />
                </Box>
                <Box className="box-note-detail-content">
                  <Stack direction="column" gap={2} flex={1}>
                    <Text fontSize="10px" color="#A0AEC0" fontWeight="medium">
                      {post.detailLink}
                    </Text>
                    <Text
                      fontSize="18px"
                      fontWeight="bold"
                      color="#2D3748"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {post.title}
                    </Text>
                    <Text
                      fontSize="12px"
                      color="#A0AEC0"
                      lineHeight="150%"
                      letterSpacing="0"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                      className='txt-note-describe'
                      dangerouslySetInnerHTML={{ __html: stripImages(post.description || '') }}
                    />
                  </Stack>
                  <Flex justifyContent="space-between" mt="10px">
                    <Switch
                      className="note-page-switch"
                      key={post.id}
                      checked={post.status === NotePostStatus.APPROVED ? true : false}
                      onChange={(e: any) => handleCheckedSwitch(post.id, e.target.checked)}
                    >
                      {trans('note.noteExpress')}
                    </Switch>
                    <a href={post.detailLink} target="_blank">
                      <Button
                        size="sm"
                        variant="outline"
                        width="107.5px"
                        height="35px"
                        borderColor="#4FD1C5"
                        color="#4FD1C5"
                        fontWeight="bold"
                        justifyContent="center"
                        _hover={{
                          bg: '#4FD1C5',
                          color: 'white',
                        }}
                      >
                        {trans('note.openPage')}
                      </Button>
                    </a>
                  </Flex>
                </Box>
              </Stack>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

export default NoteEditPage;
